import {createSelector, type Selector} from 'reselect';

import {type OrganizedSettings} from '../../../components/PanelBank/types';
import {type MediaBrowserPanelConfig} from '../../../components/PanelMediaBrowser/common';
import {
  type Complete,
  type LinePlotSettings,
  type WorkspaceLayoutSettings,
} from '../../../components/WorkspaceDrawer/Settings/types';
import {
  makeSectionLinePlotPanelsSelector,
  makeSectionSettingsSelector,
} from '../../../state/views/panelBankSectionConfig/selectors';
import {type Ref as SectionRef} from '../../../state/views/panelBankSectionConfig/types';
import {type RootState} from '../../../types/redux';
import {makeAllLinePlotSectionsAndPanelsSelector} from '../panelBankConfig/selectors';
import {type Ref as PanelBankConfigRef} from '../panelBankConfig/types';
import {makePartSelector} from '../selectors';
import {
  getPanelsThatOverrideWorkspaceSettings,
  getSectionsThatOverrideWorkspaceSettings,
} from './settingOverrideUtils';
import {type Ref as WorkspaceSettingsRef} from './types';

/**
 * Base selector: get workspace settings from normalized state by ref.
 */
export const makeWorkspaceSettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef
) => makePartSelector(workspaceSettingsRef);

export const makeWorkspaceSettingsLinePlotSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef
): Selector<RootState, OrganizedSettings['linePlot']> =>
  createSelector(
    makeWorkspaceSettingsSelector(workspaceSettingsRef),
    settings => settings.linePlot
  );

export const makeWorkspaceSettingsCoordinateZoomingSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef
): Selector<RootState, LinePlotSettings['coordinateZooming']> =>
  createSelector(
    makeWorkspaceSettingsLinePlotSelector(workspaceSettingsRef),
    linePlot => linePlot?.coordinateZooming
  );

export const makeWorkspaceSettingsShowXRangeSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, LinePlotSettings['showXRange']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsLinePlotSelector(workspaceSettingsRef),
    linePlot => linePlot?.showXRange
  );
};

export const makeWorkspaceSettingsSemanticLegendRunColorOptionsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, OrganizedSettings['semanticLegendRunColorOptions']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsSelector(workspaceSettingsRef),
    settings => settings.semanticLegendRunColorOptions
  );
};

export const makeWorkspaceSettingsShowEmptySectionsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef
): Selector<RootState, OrganizedSettings['showEmptySections']> =>
  createSelector(
    makeWorkspaceSettingsSelector(workspaceSettingsRef),
    settings => settings.showEmptySections
  );

export const makeWorkspaceSettingsAutoOrganizePrefixSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef
): Selector<RootState, OrganizedSettings['autoOrganizePrefix']> =>
  createSelector(
    makeWorkspaceSettingsSelector(workspaceSettingsRef),
    settings => settings.autoOrganizePrefix
  );

export const makeWorkspaceSettingsSortAlphabeticallySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef
): Selector<RootState, OrganizedSettings['sortAlphabetically']> =>
  createSelector(
    makeWorkspaceSettingsSelector(workspaceSettingsRef),
    settings => settings.sortAlphabetically
  );

export const makeWorkspaceSettingsShouldAutoGeneratePanelsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef
): Selector<
  RootState,
  NonNullable<WorkspaceLayoutSettings['shouldAutoGeneratePanels']>
> =>
  createSelector(
    makeWorkspaceSettingsSelector(workspaceSettingsRef),
    settings => settings.shouldAutoGeneratePanels ?? true
  );

export const makeWorkspaceLayoutSettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef
): Selector<RootState, Complete<WorkspaceLayoutSettings>> =>
  createSelector(
    makeWorkspaceSettingsShowEmptySectionsSelector(workspaceSettingsRef),
    makeWorkspaceSettingsShouldAutoGeneratePanelsSelector(workspaceSettingsRef),
    makeWorkspaceSettingsAutoOrganizePrefixSelector(workspaceSettingsRef),
    makeWorkspaceSettingsSortAlphabeticallySelector(workspaceSettingsRef),
    (
      showEmptySections,
      shouldAutoGeneratePanels,
      autoOrganizePrefix,
      sortAlphabetically
    ) => ({
      showEmptySections,
      shouldAutoGeneratePanels,
      autoOrganizePrefix,
      sortAlphabetically,
    })
  );

export const makeWorkspaceSettingsMediaSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef
): Selector<RootState, OrganizedSettings['media']> =>
  createSelector(
    makeWorkspaceSettingsSelector(workspaceSettingsRef),
    settings => settings.media
  );

export const makeWorkspaceMediaSliderSyncSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['sliderSync']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.sliderSync
  );
};

export const makeWorkspaceMediaSliderKeySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['mediaSliderKey']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.mediaSliderKey
  );
};

export const makeWorkspaceMediaModeSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['mode']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.mode
  );
};

export const makeWorkspaceMediaSnapToExistingStepSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['snapToExistingStep']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.snapToExistingStep
  );
};

export const makeWorkspaceMediaMaxRunsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['maxRuns']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.maxRuns
  );
};

export const makeWorkspaceMediaMaxGalleryItemsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['maxGalleryItems']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.maxGalleryItems
  );
};

export const makeWorkspaceMediaMaxYAxisCountSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['maxYAxisCount']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.maxYAxisCount
  );
};

export const makeWorkspaceMediaColumnCountSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['columnCount']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.columnCount
  );
};

export const makeWorkspaceMediaActualSizeSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['actualSize']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.actualSize
  );
};

export const makeWorkspaceMediaFitToDimensionSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['fitToDimension']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.fitToDimension
  );
};

export const makeWorkspaceMediaGallerySettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['gallerySettings']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.gallerySettings
  );
};

export const makeWorkspaceMediaGridSettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['gridSettings']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.gridSettings
  );
};

export const makeWorkspaceMediaAlwaysOriginalSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['alwaysOriginal']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.alwaysOriginal
  );
};

export const makeWorkspaceMediaPixelatedSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['pixelated']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.pixelated
  );
};

export const makeWorkspaceMediaStepStrideLengthSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['stepStrideLength']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.stepStrideLength
  );
};

export const makeMaybeWorkspaceSettingsMediaSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, OrganizedSettings['media'] | undefined> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return makeWorkspaceSettingsMediaSelector(workspaceSettingsRef);
};

export const makeWorkspaceMediaCompareSettingsSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['compareSettings']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.compareSettings
  );
};

export const makeWorkspaceMediaBoundingBoxConfigSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['boundingBoxConfig']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.boundingBoxConfig
  );
};

export const makeWorkspaceMediaSegmentationMaskConfigSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['segmentationMaskConfig']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.segmentationMaskConfig
  );
};

export const makeWorkspaceMediaUseRightHandedSystemSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['useRightHandedSystem']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.useRightHandedSystem
  );
};

export const makeWorkspaceMediaPointColorSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['pointColor']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.pointColor
  );
};

export const makeWorkspaceMediaObject3DBackgroundColorSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['object3DBackgroundColor']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.object3DBackgroundColor
  );
};

export const makeWorkspaceMediaDisableVideoStreamSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['disableVideoStream']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.disableVideoStream
  );
};

export const makeWorkspaceMediaVideoPlayerTimeStepSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['videoPlayerTimeStep']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.videoPlayerTimeStep
  );
};

export const makeWorkspaceMediaVideoPlaybackSyncSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['videoPlaybackSync']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.videoPlaybackSync
  );
};

export const makeWorkspaceSettingsVideoPlaybackSyncAutoplaySelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<
  RootState,
  MediaBrowserPanelConfig['videoPlaybackSyncAutoplay']
> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.videoPlaybackSyncAutoplay
  );
};

export const makeWorkspaceSettingsVideoPlaybackSyncLoopSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, MediaBrowserPanelConfig['videoPlaybackSyncLoop']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsMediaSelector(workspaceSettingsRef),
    media => media?.videoPlaybackSyncLoop
  );
};

export const makeWorkspaceSettingsColorPaletteOptionSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, OrganizedSettings['colorPaletteOption']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsSelector(workspaceSettingsRef),
    settings => settings.colorPaletteOption
  );
};

export const makeWorkspaceSettingsSemanticLegendPaletteSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined
): Selector<RootState, OrganizedSettings['semanticLegendPalette']> => {
  if (workspaceSettingsRef == null) {
    return () => undefined;
  }

  return createSelector(
    makeWorkspaceSettingsSelector(workspaceSettingsRef),
    settings => settings.semanticLegendPalette
  );
};

export const makeWorkspaceSettingOverridesSelector = (
  workspaceSettingsRef: WorkspaceSettingsRef,
  panelBankConfigRef: PanelBankConfigRef
) => {
  return createSelector(
    makePartSelector(workspaceSettingsRef),
    makeAllLinePlotSectionsAndPanelsSelector(panelBankConfigRef),
    (workspaceSettings, {sections, panels}) => {
      const overrides = {
        panelOverrides: getPanelsThatOverrideWorkspaceSettings(
          panels,
          workspaceSettings
        ),
        sectionOverrides: getSectionsThatOverrideWorkspaceSettings(
          sections,
          workspaceSettings
        ),
      };

      return overrides;
    }
  );
};

export const makeSectionSettingOverridesSelector = (sectionRef: SectionRef) => {
  return createSelector(
    makeSectionSettingsSelector(sectionRef),
    makeSectionLinePlotPanelsSelector(sectionRef),
    (sectionSettings, panels) => {
      const overrides = {
        panelOverrides: getPanelsThatOverrideWorkspaceSettings(
          panels,
          sectionSettings
        ),
      };
      return overrides;
    }
  );
};
